import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { FiSave, FiX, FiRefreshCw, FiVideo } from 'react-icons/fi';
import { mockInterviewsAPI, studentsAPI, settingsAPI, zoomAPI, usersAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { getTodayDateInputMin, isDateTimeLocalInFuture, storedDatetimeToLocalInput } from '../../utils/futureDateTime';
import { resolveDefaultInterviewTimezone } from '../../utils/browserTimezone';
import {
  formatInterviewSlotAvailabilitySuffix,
  canBypassInterviewSlotCapacity
} from '../../utils/interviewSlotAvailabilityLabel';
import { useAuth } from '../../context/AuthContext';
import { useCountry } from '../../context/CountryContext';
import AuditTimeline from '../../components/Common/AuditTimeline';
import { useTranslation } from 'react-i18next';

const INTERVIEW_STATUSES = ['scheduled', 'completed', 'cancelled', 'no_show', 'student_did_not_join', 'rescheduled'];
const INTERVIEW_OUTCOMES = ['pending', 'pass', 'fail', 'student_did_not_join'];

const formatOutcomeLabel = (outcome, t) => {
  const translated = t(`mockInterviews.outcomes.${outcome}`, {
    defaultValue: t(`interviews.outcomes.${outcome}`, { defaultValue: '' })
  });
  if (translated) return translated;
  return String(outcome)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

/**
 * Human-readable label for a mock-interview status code. Replaces every
 * underscore (not just the first) and title-cases each word, so
 * `student_did_not_join` shows as "Student Did Not Join" in the dropdown.
 */
const formatInterviewStatus = (status, t) => {
  if (!status) return '';
  const translated = t
    ? t(`mockInterviews.status.${status}`, {
        defaultValue: t(`interviews.status.${status}`, { defaultValue: '' }),
      })
    : '';
  if (translated) return translated;
  return String(status)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const MockInterviewForm = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { getConfig, activeCountry } = useCountry();
  const config = getConfig();
  const { id, studentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isStudentContextLocked = Boolean(studentId || location.state?.fromStudentId);
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [timezones, setTimezones] = useState([]);
  const [zoomConfigured, setZoomConfigured] = useState(false);
  const [zoomGenerating, setZoomGenerating] = useState(false);
  const [initialScheduledAt, setInitialScheduledAt] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [slotDate, setSlotDate] = useState('');
  const [slotStartTime, setSlotStartTime] = useState('');
  const [slotOptions, setSlotOptions] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [zoomHosts, setZoomHosts] = useState([]);
  const [form, setForm] = useState({
    student_id: studentId || '',
    scheduled_at: '',
    timezone: config.defaultTimezone,
    interview_link: '',
    status: 'scheduled',
    remarks: '',
    outcome: 'pending',
    interview_host_user_id: '',
    zoom_email_used: '',
    zoom_meeting_id: '',
    zoom_join_url: '',
    zoom_start_url: '',
    zoom_passcode: ''
  });

  /** Resolved via API — list from getAll() is paginated (default 25), so the selected student is often missing from `students`. */
  const [resolvedStudentLanguage, setResolvedStudentLanguage] = useState(null);

  useEffect(() => {
    if (!form.student_id) {
      setResolvedStudentLanguage(null);
      return;
    }
    let cancelled = false;
    studentsAPI
      .getById(form.student_id)
      .then((res) => {
        if (cancelled) return;
        const lang = res.data?.language;
        setResolvedStudentLanguage(lang === 'FRENCH' ? 'FRENCH' : 'ENGLISH');
      })
      .catch(() => {
        if (!cancelled) setResolvedStudentLanguage(null);
      });
    return () => {
      cancelled = true;
    };
  }, [form.student_id]);

  const isFranceCRM = String(activeCountry || '').toUpperCase() === 'FRANCE';
  const needsInterviewHost = isFranceCRM && resolvedStudentLanguage === 'FRENCH';
  const selectedStudent = students.find((s) => String(s.student_id) === String(form.student_id));
  const selectedCampusId = selectedStudent?.campus_id || null;

  const scheduledAtFromSlot = (dateStr, hhmm) => {
    if (!dateStr || !hhmm) return '';
    return `${String(dateStr).slice(0, 10)}T${String(hhmm).slice(0, 5)}`;
  };

  const getReturnPath = () => {
    const fromStudentId = location.state?.fromStudentId;
    if (fromStudentId) return `/students/${fromStudentId}`;
    if (studentId) return `/students/${studentId}`;
    if (form.student_id) return `/students/${form.student_id}`;
    return '/mock-interviews';
  };

  useEffect(() => {
    studentsAPI
      .getAll({ page: 1, limit: 5000 })
      .then((r) => {
        const studentsData = r.data.students || r.data || [];
        setStudents(studentsData);
      })
      .catch(() => {});
    settingsAPI.getInterviewTimezones().then((res) => {
      const activeZones = (res.data || []).filter((tz) => tz.status === 'active');
      setTimezones(activeZones);
      if (activeZones.length > 0 && !id) {
        setForm((prev) => ({ ...prev, timezone: resolveDefaultInterviewTimezone(activeZones, config.defaultTimezone) }));
      }
    }).catch(() => {});

    zoomAPI.getStatus().then(r => setZoomConfigured(r.data.configured)).catch(() => {});

    if (id) {
      setLoading(true);
      mockInterviewsAPI.getById(id)
        .then(res => {
          const interview = res.data;
          const tz = interview.timezone || resolveDefaultInterviewTimezone(timezones, config.defaultTimezone);
          const scheduledDateTime = interview.scheduled_at ? storedDatetimeToLocalInput(interview.scheduled_at) : '';
          const initDate = scheduledDateTime ? scheduledDateTime.slice(0, 10) : '';
          const initTime = scheduledDateTime ? scheduledDateTime.slice(11, 16) : '';
          setForm({
            student_id: interview.student_id.toString(),
            scheduled_at: scheduledDateTime,
            timezone: tz,
            interview_link: interview.interview_link || '',
            status: interview.status || 'scheduled',
            remarks: interview.remarks || '',
            outcome: interview.outcome || 'pending',
            interview_host_user_id:
              interview.interview_host_user_id != null ? String(interview.interview_host_user_id) : '',
            zoom_email_used: interview.zoom_email_used || '',
            zoom_meeting_id: interview.zoom_meeting_id || '',
            zoom_join_url: interview.zoom_join_url || '',
            zoom_start_url: interview.zoom_start_url || '',
            zoom_passcode: interview.zoom_passcode || ''
          });
          setSlotDate(initDate);
          setSlotStartTime(initTime);
          setInitialScheduledAt(scheduledDateTime);
        })
        .catch(() => {
          toast.error(t('mockInterviews.failedLoadSingle'));
          navigate(getReturnPath());
        })
        .finally(() => setLoading(false));
    } else {
      setInitialScheduledAt(null);
      const today = getTodayDateInputMin();
      setSlotDate(today);
      setSlotStartTime('');
    }
  }, [id, studentId, navigate, config.defaultTimezone]);

  useEffect(() => {
    if (!needsInterviewHost) {
      setZoomHosts([]);
      return;
    }
    usersAPI.getInterviewZoomHosts().then((r) => setZoomHosts(r.data || [])).catch(() => setZoomHosts([]));
  }, [needsInterviewHost]);

  useEffect(() => {
    if (!needsInterviewHost) {
      setForm((f) => {
        if (!f.interview_host_user_id && !f.zoom_email_used) return f;
        return { ...f, interview_host_user_id: '', zoom_email_used: '' };
      });
    }
  }, [needsInterviewHost]);

  useEffect(() => {
    if (!slotDate) {
      setSlotOptions([]);
      return;
    }
    setSlotsLoading(true);
    settingsAPI
      .getInterviewSlotAvailability({ date: slotDate, type: 'mock', campus_id: selectedCampusId || undefined })
      .then((res) => setSlotOptions(res.data || []))
      .catch(() => setSlotOptions([]))
      .finally(() => setSlotsLoading(false));
  }, [slotDate, selectedCampusId]);

  useEffect(() => {
    const next = scheduledAtFromSlot(slotDate, slotStartTime);
    setForm((f) => ({ ...f, scheduled_at: next }));
  }, [slotDate, slotStartTime]);

  useEffect(() => {
    if (!id) {
      setHistory([]);
      setHistoryLoading(false);
      return;
    }
    setHistoryLoading(true);
    settingsAPI
      .getAuditLog({ entity_type: 'interview', entity_id: id, limit: 200, page: 1 })
      .then((r) => setHistory(r.data || []))
      .catch(() => setHistory([]))
      .finally(() => setHistoryLoading(false));
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.student_id || !form.scheduled_at || !form.timezone) {
      toast.error(t('mockInterviews.fillRequired'));
      return;
    }
    const slotChanged = !id || form.scheduled_at !== initialScheduledAt;
    if (slotChanged && !isDateTimeLocalInFuture(form.scheduled_at)) {
      toast.error(t('mockInterviews.futureDateRequired'));
      return;
    }
    if (needsInterviewHost && !form.interview_host_user_id) {
      toast.error(t('interviews.frenchHostRequired'));
      return;
    }

    setLoading(true);
    try {
      const payload = {
        scheduled_at: form.scheduled_at,
        timezone: form.timezone,
        interview_link: form.interview_link || null,
        remarks: form.remarks || null,
        zoom_meeting_id: form.zoom_meeting_id || null,
        zoom_join_url: form.zoom_join_url || null,
        zoom_start_url: form.zoom_start_url || null,
        zoom_passcode: form.zoom_passcode || null,
        interview_host_user_id: needsInterviewHost ? form.interview_host_user_id || null : null,
        zoom_email_used: needsInterviewHost ? form.zoom_email_used || null : null
      };
      if (id) {
        payload.status = form.status;
        payload.outcome = form.outcome;
        await mockInterviewsAPI.update(id, payload);
        toast.success(t('mockInterviews.updatedSuccess'));
      } else {
        await mockInterviewsAPI.create(form.student_id, payload);
        toast.success(t('mockInterviews.scheduledSuccess'));
      }
      navigate(getReturnPath());
    } catch (err) {
      toast.error(err.response?.data?.message || t('mockInterviews.saveFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (loading && id) return <div className="loading-spinner"><div className="spinner"></div></div>;

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>{id ? t('mockInterviews.editTitle') : t('mockInterviews.scheduleTitle')}</h1>
          <p>{id ? t('mockInterviews.editSubtitle') : t('mockInterviews.scheduleSubtitle')}</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {id && (
            <button className="btn btn-outline" onClick={() => {
              setForm({ ...form, status: 'rescheduled' });
              toast.info(t('mockInterviews.updateRescheduleHint'));
            }}>
              <FiRefreshCw /> {t('mockInterviews.rescheduleBtn')}
            </button>
          )}
          <button className="btn btn-outline" onClick={() => navigate(getReturnPath())}>
            <FiX /> {t('common.cancel')}
          </button>
        </div>
      </div>

      <div className="card">
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label>{t('interviews.studentLabel')} <span style={{ color: 'var(--danger)' }}>*</span></label>
              <select
                value={form.student_id}
                onChange={(e) => setForm({ ...form, student_id: e.target.value })}
                disabled={!!id || isStudentContextLocked}
                required
              >
                <option value="">{t('interviews.selectStudent')}</option>
                {students.map(s => <option key={s.student_id} value={s.student_id}>{s.student_ref} - {s.title} {s.first_name} {s.last_name}</option>)}
              </select>
              {form.student_id && resolvedStudentLanguage && (
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                  {t('studentForm.languageLabel')}:{' '}
                  {resolvedStudentLanguage === 'FRENCH'
                    ? t('studentForm.languageFrench')
                    : t('studentForm.languageEnglish')}
                </p>
              )}
            </div>

            {needsInterviewHost && (
              <>
                <div className="form-group">
                  <label>
                    {t('interviews.interviewHostLabel')}{' '}
                    <span style={{ color: 'var(--danger)' }}>*</span>
                  </label>
                  <select
                    value={form.interview_host_user_id}
                    onChange={(e) => {
                      const v = e.target.value;
                      const h = zoomHosts.find((x) => String(x.user_id) === String(v));
                      setForm((f) => ({
                        ...f,
                        interview_host_user_id: v,
                        zoom_email_used: h?.zoom_meeting_email || ''
                      }));
                    }}
                    required
                  >
                    <option value="">{t('interviews.selectInterviewHost')}</option>
                    {form.interview_host_user_id &&
                      !zoomHosts.some((h) => String(h.user_id) === String(form.interview_host_user_id)) && (
                        <option value={form.interview_host_user_id}>
                          {form.zoom_email_used || `#${form.interview_host_user_id}`}
                        </option>
                      )}
                    {zoomHosts.map((h) => (
                      <option key={h.user_id} value={h.user_id}>
                        {h.name} — {h.zoom_meeting_email}
                      </option>
                    ))}
                  </select>
                  {zoomHosts.length === 0 && (
                    <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                      {t('interviews.noZoomHostsConfigured')}
                    </p>
                  )}
                </div>
                {form.interview_host_user_id && form.zoom_email_used && (
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <p style={{ fontSize: 13, margin: 0, color: 'var(--text-secondary)' }}>
                      <strong>{t('interviews.hostZoomAccount')}</strong> {form.zoom_email_used}
                    </p>
                  </div>
                )}
              </>
            )}

            <div className="form-group">
              <label>{t('interviews.dateLabel')} <span style={{ color: 'var(--danger)' }}>*</span></label>
              <input
                type="date"
                value={slotDate}
                min={getTodayDateInputMin()}
                onChange={(e) => {
                  setSlotDate(e.target.value);
                  setSlotStartTime('');
                }}
                required
              />
            </div>

            <div className="form-group">
              <label>{t('interviews.slotLabel')} <span style={{ color: 'var(--danger)' }}>*</span></label>
              <select
                value={slotStartTime}
                onChange={(e) => setSlotStartTime(e.target.value)}
                required
                disabled={!slotDate || slotsLoading}
              >
                <option value="">
                  {slotsLoading ? t('studentDetail.modals.loadingSlots') : (slotOptions.length ? t('studentDetail.modals.selectSlot') : t('studentDetail.modals.noSlotsConfigured'))}
                </option>
                {slotOptions.map((s) => {
                  const value = String(s.start_time || '').slice(0, 5);
                  const label = s.label || `${value} - ${String(s.end_time || '').slice(0, 5)}`;
                  const isFull = !!s.is_full;
                  const bypass = canBypassInterviewSlotCapacity(user);
                  const disabled = !bypass && isFull;
                  const capText = formatInterviewSlotAvailabilitySuffix(s);
                  return (
                    <option key={s.slot_id} value={value} disabled={disabled}>
                      {label} — {capText}{disabled ? ` ${t('studentDetail.modals.full')}` : ''}
                    </option>
                  );
                })}
              </select>
              {canBypassInterviewSlotCapacity(user) && (
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                  {t('interviews.adminCapacity')}
                </p>
              )}
            </div>

            <div className="form-group">
              <label>{t('interviews.timezoneLabel')} <span style={{ color: 'var(--danger)' }}>*</span></label>
              <select value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} required>
                {timezones.length > 0 ? timezones.map((tz) => (
                  <option key={tz.timezone_id} value={tz.timezone_name}>{tz.timezone_label || tz.timezone_name}</option>
                )) : <option value={config.defaultTimezone}>{config.defaultTimezone} (default)</option>}
              </select>
            </div>

            <div className="form-group">
              <label>{t('interviews.interviewLink')}</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="url" value={form.interview_link} onChange={(e) => setForm({ ...form, interview_link: e.target.value })} placeholder={t('interviews.linkPlaceholder')} style={{ flex: 1 }} />
                {zoomConfigured && form.scheduled_at && (
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}
                    disabled={zoomGenerating || (needsInterviewHost && !form.interview_host_user_id)}
                    onClick={async () => {
                      if (needsInterviewHost && !form.interview_host_user_id) {
                        toast.error(t('interviews.frenchHostRequired'));
                        return;
                      }
                      setZoomGenerating(true);
                      try {
                        const { data } = await zoomAPI.generate({
                          topic: `Mock Interview - ${students.find(s => String(s.student_id) === String(form.student_id))?.first_name || 'Student'}`,
                          start_time: form.scheduled_at,
                          duration: 60,
                          timezone: form.timezone || resolveDefaultInterviewTimezone(timezones, config.defaultTimezone),
                          entity_type: 'mock_interview',
                          entity_id: id || undefined,
                          host_user_id: needsInterviewHost ? form.interview_host_user_id : undefined
                        });
                        setForm((f) => ({
                          ...f,
                          interview_link: data.join_url,
                          zoom_meeting_id: data.zoom_meeting_id,
                          zoom_join_url: data.join_url,
                          zoom_start_url: data.start_url,
                          zoom_passcode: data.passcode,
                          zoom_email_used: data.zoom_email_used || f.zoom_email_used,
                          interview_host_user_id:
                            data.interview_host_user_id != null
                              ? String(data.interview_host_user_id)
                              : f.interview_host_user_id
                        }));
                        toast.success(t('mockInterviews.zoomGenerated'));
                      } catch (err) {
                        toast.error(err.response?.data?.message || t('mockInterviews.zoomFailed'));
                      }
                      setZoomGenerating(false);
                    }}
                  >
                    <FiVideo size={14} /> {zoomGenerating ? t('interviews.creatingZoom') : t('interviews.generateZoom')}
                  </button>
                )}
              </div>
            </div>

            {id && (
              <div className="form-group">
                <label>{t('common.status')}</label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  {INTERVIEW_STATUSES.map(s => <option key={s} value={s}>{formatInterviewStatus(s, t)}</option>)}
                </select>
              </div>
            )}

            {id && (
              <div className="form-group">
                <label>{t('interviews.outcomeLabel')}</label>
                <select value={form.outcome} onChange={(e) => setForm({ ...form, outcome: e.target.value })}>
                  {INTERVIEW_OUTCOMES.map(o => <option key={o} value={o}>{formatOutcomeLabel(o, t)}</option>)}
                </select>
              </div>
            )}

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>{t('interviews.remarksLabel')}</label>
              <textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} rows={6} placeholder={t('mockInterviews.remarksPlaceholder')} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 24 }}>
            <button type="button" className="btn btn-outline" onClick={() => navigate(getReturnPath())}>{t('common.cancel')}</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              <FiSave /> {loading ? t('mockInterviews.savingBtn') : (id ? t('mockInterviews.updateBtn') : t('mockInterviews.scheduleBtn'))}
            </button>
          </div>
        </form>
      </div>

      {id && (
        <div className="card" style={{ padding: 0, overflow: 'hidden', marginTop: 12 }}>
          <h3 style={{ margin: '16px 20px 12px', fontWeight: 800 }}>{t('common.history')}</h3>
          <div style={{ padding: '0 0 12px' }}>
            {historyLoading ? (
              <div className="loading-spinner"><div className="spinner" /></div>
            ) : (
              <AuditTimeline
                logs={history}
                showEntity={false}
                filterLog={(log) => {
                  const v = (x) => {
                    try {
                      const o = typeof x === 'string' ? JSON.parse(x) : x;
                      return o && typeof o === 'object' ? o : {};
                    } catch {
                      return {};
                    }
                  };
                  const oldObj = v(log.old_values);
                  const newObj = v(log.new_values);
                  const t = newObj.interview_type ?? oldObj.interview_type;
                  return t === 'mock';
                }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MockInterviewForm;
